import { LoadingState, loadingReducers } from './base/baseModelStore';
import { ChamberCountPerStatus, EventCountPerStatus, AccountChambersBound } from '../api-model/models';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { httpClient } from '../infrastructure/http/HttpClient';
import { EventsHistogramType } from './events';
import { AppThunk } from '@root/store/store';
import { AdditionalAction, dispatchAdditionalActions } from './base/baseModelThunks';

export interface DashboardStats extends LoadingState {
  eventsHistogram: EventsHistogramType;
  countPerStatus: ChamberCountPerStatus;
  totalOngoingEvents: number;
  lastDayCountPerStatus: EventCountPerStatus;
  chambersBound: AccountChambersBound;
  latestUpdatedChamberDate?: string; // as Date
}

const setLatestChamberUpdateDate = (state: DashboardStats, { payload }: PayloadAction<string>) => {
  state.latestUpdatedChamberDate = payload;
};

const loadingSuccess = (
  state: DashboardStats,
  {
    payload,
  }: PayloadAction<{
    events: EventsHistogramType;
    chamberStats: ChamberCountPerStatus;
    lastDayStats: EventCountPerStatus;
    accountChambersBound: AccountChambersBound;
  }>
) => {
  state.eventsHistogram = payload.events;
  state.countPerStatus = payload.chamberStats;
  state.lastDayCountPerStatus = payload.lastDayStats;
  state.chambersBound = payload.accountChambersBound;
  state.loading = false;
};

const setTotalOngoingEvents = (state: DashboardStats, { payload }: PayloadAction<number>) => {
  state.totalOngoingEvents = payload;
};

const slice = createSlice({
  name: 'dashboardStats',
  initialState: {
    loading: false,
    error: null,
    countPerStatus: { maintenanceStatus: {}, status: {}, quietChamberCount: 0 },
    eventsHistogram: {},
    lastDayCountPerStatus: { maintenanceStatus: {}, status: {} },
    totalOngoingEvents: 0,
  } as DashboardStats,
  reducers: {
    ...loadingReducers,
    loadingSuccess,
    setTotalOngoingEvents,
    setLatestChamberUpdateDate,
  },
});

const actions = slice.actions;

const fetchAccountStats =
  (additionalActions?: ((response: ChamberCountPerStatus) => AdditionalAction)[]): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(actions.startLoading());

      const [
        { data: countPerStatus },
        { data: eventsHistogram },
        { data: lastDayCountPerStatus },
        { data: accountChambersBound },
      ] = await Promise.all([
        httpClient.get<ChamberCountPerStatus>(`chambers/stats/current`),
        httpClient.get<EventsHistogramType>(`events/account-histogram`),
        httpClient.get<EventCountPerStatus>(`events/stats`),
        httpClient.get<AccountChambersBound>(`chambers/account/bound`),
      ]);

      dispatch(
        actions.loadingSuccess({
          events: eventsHistogram,
          chamberStats: (countPerStatus ?? {
            maintenanceStatus: {},
            status: {},
            quietChamberCount: 0,
          }) as ChamberCountPerStatus,
          lastDayStats: (lastDayCountPerStatus ?? {
            maintenanceStatus: {},
            status: {},
          }) as EventCountPerStatus,
          accountChambersBound: accountChambersBound,
        })
      );

      if (additionalActions) {
        dispatchAdditionalActions(dispatch, additionalActions, countPerStatus as ChamberCountPerStatus);
      }
    } catch (err) {
      dispatch(actions.loadingFailed(err.toString()));
    }
  };

const thunks = {
  fetchAccountStats,
};

export const dashboardStats = {
  reducer: slice.reducer,
  actions,
  thunks,
} as {
  reducer: typeof slice.reducer;
  actions: typeof actions;
  thunks: typeof thunks;
};
